import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  ChevronDown,
  User,
  LogOut,
  LayoutDashboard,
  Moon,
  Sun,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LangContext';
import { useTheme } from '@/contexts/ThemeContext';
import api from '@/lib/api';

const STATIC_LANGUAGE_ORDER = [
  { code: 'ar', label: 'Arabic', labelAr: 'العربية' },
  { code: 'en', label: 'English', labelAr: 'الإنجليزية' },
  { code: 'fr', label: 'French', labelAr: 'الفرنسية' },
  { code: 'es', label: 'Spanish', labelAr: 'الإسبانية' },
  { code: 'de', label: 'German', labelAr: 'الألمانية' },
  { code: 'it', label: 'Italian', labelAr: 'الإيطالية' },
  { code: 'ru', label: 'Russian', labelAr: 'الروسية' },
  { code: 'zh', label: 'Chinese', labelAr: 'الصينية' },
  { code: 'pt', label: 'Portuguese', labelAr: 'البرتغالية' },
  { code: 'tr', label: 'Turkish', labelAr: 'التركية' },
  { code: 'hi', label: 'Hindi', labelAr: 'الهندية' },
  { code: 'ja', label: 'Japanese', labelAr: 'اليابانية' },
];

const PLACEMENT_LANGUAGE_CODES = new Set(['ar', 'en']);
const PLACEMENT_LANGUAGE_NAMES = new Set(['arabic', 'english', 'العربية', 'الانجليزية', 'الإنجليزية']);

function toSearch(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

function createLanguageHref(languageId) {
  return `/courses${toSearch({ view: 'language', audience: 'individual', language_id: languageId })}`;
}

function createPlacementHref(languageId, courseTypeId) {
  return `/placement-test${toSearch({ language_id: languageId, course_type_id: courseTypeId })}`;
}

function getPlacementCourseTypeId(languageId, courseTypes) {
  const items = courseTypes.filter((item) => String(item.language_id) === String(languageId));
  return items[0]?.id || '';
}

function createCourseHref(languageId, course) {
  return `/courses/${course.slug}${toSearch({
    view: 'language',
    audience: 'individual',
    language_id: languageId,
    course_type: course.course_type_id,
  })}`;
}

function normalizeLanguageName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isPlacementLanguage(language) {
  const code = String(language?.code || '').trim().toLowerCase();
  if (PLACEMENT_LANGUAGE_CODES.has(code)) return true;
  return PLACEMENT_LANGUAGE_NAMES.has(normalizeLanguageName(language?.name));
}

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeLanguageCode, setActiveLanguageCode] = useState('');
  const [navSignal, setNavSignal] = useState({ left: 0, width: 0, opacity: 0 });
  const navWrapRef = useRef(null);
  const navItemRefs = useRef({});
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lang, toggleLang, t } = useLang();
  const { toggleTheme, isDark } = useTheme();
  const isRTL = lang === 'ar';
  const isHomeRoute = location.pathname === '/';
  const isCourseDetailRoute = location.pathname.startsWith('/courses/');
  const isHeroOverlayRoute = isHomeRoute || isCourseDetailRoute;
  const glassHeaderActive = scrolled || !isHeroOverlayRoute;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/languages').catch(() => ({ data: [] })),
      api.get('/course-types').catch(() => ({ data: [] })),
      api.get('/courses', { params: { audience: 'individual' } }).catch(() => ({ data: [] })),
    ]).then(([langRes, typeRes, courseRes]) => {
      setLanguages(Array.isArray(langRes.data) ? langRes.data : []);
      setCourseTypes(Array.isArray(typeRes.data) ? typeRes.data : []);
      setCourses(Array.isArray(courseRes.data) ? courseRes.data : []);
    });
  }, [lang]);

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'teacher' || user.role === 'admin') return '/dashboard/teacher';
    return '/dashboard/student';
  };

  const activeNavKey = useMemo(() => {
    if (location.pathname === '/') return 'home';
    if (location.pathname.startsWith('/courses')) return 'courses';
    if (location.pathname.startsWith('/placement-test')) return 'placement';
    if (location.pathname === '/corporate') return 'corporate';
    if (location.pathname === '/about') return 'about';
    if (location.pathname === '/contact') return 'contact';
    return '';
  }, [location.pathname]);

  const moveNavSignal = (node) => {
    const wrapper = navWrapRef.current;
    if (!wrapper || !node) {
      setNavSignal((current) => ({ ...current, opacity: 0 }));
      return;
    }
    const wrapperRect = wrapper.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    setNavSignal({
      left: nodeRect.left - wrapperRect.left,
      width: nodeRect.width,
      opacity: 1,
    });
  };

  const moveNavSignalToActive = () => {
    moveNavSignal(navItemRefs.current[activeNavKey]);
  };

  const getNavSignalProps = (key) => ({
    ref: (node) => {
      if (node) navItemRefs.current[key] = node;
      else delete navItemRefs.current[key];
    },
    onPointerEnter: (event) => moveNavSignal(event.currentTarget),
    onPointerMove: (event) => moveNavSignal(event.currentTarget),
    onMouseEnter: (event) => moveNavSignal(event.currentTarget),
    onMouseMove: (event) => moveNavSignal(event.currentTarget),
    onFocus: (event) => moveNavSignal(event.currentTarget),
  });

  useEffect(() => {
    const syncActiveSignal = () => {
      const wrapper = navWrapRef.current;
      const node = navItemRefs.current[activeNavKey];
      if (!wrapper || !node) {
        setNavSignal((current) => ({ ...current, opacity: 0 }));
        return;
      }
      const wrapperRect = wrapper.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      setNavSignal({
        left: nodeRect.left - wrapperRect.left,
        width: nodeRect.width,
        opacity: 1,
      });
    };

    const frame = window.requestAnimationFrame(syncActiveSignal);
    window.addEventListener('resize', syncActiveSignal);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', syncActiveSignal);
    };
  }, [activeNavKey, lang]);

  const navButtonClass = (active) => `bf-header-nav-link ${active ? 'is-active' : ''}`;
  const mobileNavLinkClass = (active) => `bf-mobile-link ${active ? 'is-active' : ''}`;
  const dropdownPanelClass = 'bf-dropdown-panel';
  const dropdownItemClass = 'bf-dropdown-item rounded-xl';

  const placementLanguages = useMemo(() => {
    return languages.filter((language) => isPlacementLanguage(language));
  }, [languages]);
  const staticLanguages = useMemo(() => {
    return STATIC_LANGUAGE_ORDER.map((item) => {
      const matchedLanguage = languages.find((language) => {
        if (language.code && language.code.toLowerCase() === item.code) return true;
        return normalizeLanguageName(language.name) === normalizeLanguageName(item.label);
      });
      return {
        ...item,
        id: matchedLanguage?.id || null,
        name: lang === 'ar' ? item.labelAr : item.label,
      };
    });
  }, [lang, languages]);

  const coursesByLanguage = useMemo(() => {
    return courses.reduce((acc, course) => {
      const key = String(course.language_id || '');
      if (!key) return acc;
      if (!acc[key]) acc[key] = [];
      acc[key].push(course);
      return acc;
    }, {});
  }, [courses]);

  useEffect(() => {
    const current = staticLanguages.find((language) => language.code === activeLanguageCode);
    if (current?.id) return;
    const firstWithCourses = staticLanguages.find((language) => language.id && (coursesByLanguage[String(language.id)] || []).length > 0);
    const firstAvailable = firstWithCourses || staticLanguages.find((language) => language.id) || staticLanguages[0];
    setActiveLanguageCode(firstAvailable?.code || '');
  }, [activeLanguageCode, coursesByLanguage, staticLanguages]);

  const activeDesktopLanguage = useMemo(() => {
    return staticLanguages.find((language) => language.code === activeLanguageCode) || staticLanguages.find((language) => language.id) || staticLanguages[0];
  }, [activeLanguageCode, staticLanguages]);

  const activeDesktopLanguageCourses = activeDesktopLanguage?.id ? coursesByLanguage[String(activeDesktopLanguage.id)] || [] : [];

  const renderPlacementLink = (language) => {
    if (!language) return null;
    const courseTypeId = getPlacementCourseTypeId(language.id, courseTypes);
    return (
      <DropdownMenuItem key={`placement-${language.id}`} asChild className={dropdownItemClass}>
        <Link to={createPlacementHref(language.id, courseTypeId)}>
          <span>{language.name}</span>
        </Link>
      </DropdownMenuItem>
    );
  };

  const renderDesktopLanguageMenu = () => (
    <div className="bf-language-menu">
      <div className="bf-language-list" aria-label={t('nav.languages')}>
        {staticLanguages.map((language) => {
          const isActive = language.code === activeDesktopLanguage?.code;
          const languageCourses = language.id ? coursesByLanguage[String(language.id)] || [] : [];
          const disabled = !language.id;
          const content = (
            <>
              <span>{language.name}</span>
              {languageCourses.length > 0 ? <span className="bf-language-count">{languageCourses.length}</span> : null}
            </>
          );

          if (disabled) {
            return (
              <button key={`language-${language.code}`} type="button" className="bf-language-option is-disabled" disabled>
                {content}
              </button>
            );
          }

          return (
            <button
              key={`language-${language.code}`}
              type="button"
              className={`bf-language-option ${isActive ? 'is-active' : ''}`}
              onPointerEnter={() => setActiveLanguageCode(language.code)}
              onFocus={() => setActiveLanguageCode(language.code)}
              onClick={() => setActiveLanguageCode(language.code)}
            >
              {content}
            </button>
          );
        })}
      </div>

      <div className="bf-language-courses-panel">
        <div className="bf-language-courses-head">
          <span>{activeDesktopLanguage?.name || t('nav.languages')}</span>
          {activeDesktopLanguage?.id ? (
            <Link to={createLanguageHref(activeDesktopLanguage.id)} className="bf-language-all-link">
              {lang === 'ar' ? 'كل الدورات' : 'All courses'}
            </Link>
          ) : null}
        </div>

        <div className="bf-language-course-list">
          {activeDesktopLanguageCourses.length > 0 ? (
            activeDesktopLanguageCourses.slice(0, 8).map((course, index) => (
              <DropdownMenuItem key={course.id} asChild className={`${dropdownItemClass} bf-language-course-item`}>
                <Link to={createCourseHref(activeDesktopLanguage.id, course)} style={{ '--menu-item-delay': `${index * 32}ms` }}>
                  <span className="bf-language-course-name">{course.title}</span>
                  <span className="bf-language-course-meta">{course.course_type_name || course.modality || (lang === 'ar' ? 'دورة' : 'Course')}</span>
                </Link>
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem asChild className={`${dropdownItemClass} bf-language-course-item`}>
              <Link to={activeDesktopLanguage?.id ? createLanguageHref(activeDesktopLanguage.id) : '/courses'}>
                <span className="bf-language-course-name">{lang === 'ar' ? 'استعرض الدورات' : 'Browse available courses'}</span>
                <span className="bf-language-course-meta">{lang === 'ar' ? 'عرض كل البرامج' : 'View programs'}</span>
              </Link>
            </DropdownMenuItem>
          )}
        </div>
      </div>
    </div>
  );

  const renderMobilePlacementLinks = (language) => {
    const courseTypeId = getPlacementCourseTypeId(language.id, courseTypes);
    return (
      <SheetClose asChild key={`mobile-placement-${language.id}`}>
        <Link to={createPlacementHref(language.id, courseTypeId)} className={mobileNavLinkClass(false)}>
          {language.name}
        </Link>
      </SheetClose>
    );
  };

  const renderMobileLanguageLinks = (language) => {
    if (!language.id) {
      return (
        <div key={`mobile-language-${language.code}`} className={`${mobileNavLinkClass(false)} cursor-default opacity-60`}>
          {language.name}
        </div>
      );
    }
    const items = coursesByLanguage[String(language.id)] || [];
    if (items.length === 0) {
      return (
        <SheetClose asChild key={`mobile-language-${language.code}`}>
          <Link to={createLanguageHref(language.id)} className={mobileNavLinkClass(false)}>
            {language.name}
          </Link>
        </SheetClose>
      );
    }
    return (
      <AccordionItem
        key={`mobile-language-${language.code}`}
        value={`mobile-language-${language.code}`}
        className="bf-mobile-accordion"
      >
        <AccordionTrigger className="bf-mobile-accordion-trigger">
          {language.name}
        </AccordionTrigger>
        <AccordionContent className="space-y-1 pb-3">
          <SheetClose asChild>
            <Link to={createLanguageHref(language.id)} className="bf-mobile-inline-link">
              {lang === 'ar' ? `كل دورات ${language.name}` : `All ${language.name} courses`}
            </Link>
          </SheetClose>
          {items.map((course) => (
            <SheetClose asChild key={`mobile-language-${language.code}-course-${course.id}`}>
              <Link
                to={createCourseHref(language.id, course)}
                className="bf-mobile-course-link"
              >
                {course.title}
              </Link>
            </SheetClose>
          ))}
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <header
      data-testid="main-header"
      className={`bf-site-header ${isHeroOverlayRoute ? 'fixed' : 'sticky'} top-0 z-50 border-b transition-all duration-300 ${
        glassHeaderActive ? 'is-scrolled border-transparent' : 'is-hero-top border-transparent'
      }`}
    >
      <span className="bf-header-signal-line" aria-hidden="true" />
      <nav className="bf-header-shell mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="bf-header-logo group" data-testid="logo-link">
          <span className="bf-header-logo-glow" aria-hidden="true" />
          <img src={isDark ? '/befluent-logo-white.png' : '/befluent-logo.png'} alt={t('brand.alt')} className="relative h-11 w-auto drop-shadow-sm transition-transform duration-300 group-hover:scale-[1.025] sm:h-14" />
        </Link>

        <div
          ref={navWrapRef}
          className="bf-header-nav hidden items-center gap-1 lg:flex"
          onPointerLeave={moveNavSignalToActive}
        >
          <span
            className="bf-header-nav-signal"
            style={{
              '--bf-nav-signal-left': `${navSignal.left}px`,
              '--bf-nav-signal-width': `${navSignal.width}px`,
              opacity: navSignal.opacity,
            }}
            aria-hidden="true"
          />
          <Link to="/" className={navButtonClass(activeNavKey === 'home')} {...getNavSignalProps('home')}>{t('nav.home')}</Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className={navButtonClass(activeNavKey === 'courses')} {...getNavSignalProps('courses')}>
                {t('nav.languages')} <ChevronDown className="bf-dropdown-chevron h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" sideOffset={14} collisionPadding={16} className={`${dropdownPanelClass} bf-language-dropdown-panel p-2.5`}>
              {renderDesktopLanguageMenu()}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className={navButtonClass(activeNavKey === 'placement')} {...getNavSignalProps('placement')}>
                {t('nav.placement_test')} <ChevronDown className="bf-dropdown-chevron h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" sideOffset={14} collisionPadding={16} className={`${dropdownPanelClass} w-72 p-2.5`}>
              {placementLanguages.map((language) => renderPlacementLink(language))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/corporate" className={navButtonClass(activeNavKey === 'corporate')} {...getNavSignalProps('corporate')}>{t('nav.corporate')}</Link>
          <Link to="/about" className={navButtonClass(activeNavKey === 'about')} {...getNavSignalProps('about')}>{t('nav.about')}</Link>
          <Link to="/contact" className={navButtonClass(activeNavKey === 'contact')} {...getNavSignalProps('contact')}>{t('nav.contact')}</Link>
        </div>

        <div className="hidden items-center gap-2.5 lg:flex">
          <Button variant="ghost" size="icon" className="bf-action-chip" onClick={toggleTheme} data-testid="theme-toggle" title={isDark ? t('theme.switch_to_light') : t('theme.switch_to_dark')} aria-label={isDark ? t('theme.switch_to_light') : t('theme.switch_to_dark')}>
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="bf-action-chip" onClick={toggleLang} data-testid="lang-toggle" title={lang === 'en' ? t('lang.switch_to_ar') : t('lang.switch_to_en')} aria-label={lang === 'en' ? t('lang.switch_to_ar') : t('lang.switch_to_en')}>
            <span className="text-xs font-bold">{lang === 'en' ? t('lang.short_ar') : t('lang.short_en')}</span>
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="bf-user-chip" data-testid="user-menu-button">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 shadow-inner">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium">{user.first_name}</span>
                  <ChevronDown className="bf-dropdown-chevron h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={14} collisionPadding={16} className={`${dropdownPanelClass} w-52 p-2.5`}>
                <DropdownMenuItem onClick={() => navigate(getDashboardPath())} className={`${dropdownItemClass} cursor-pointer gap-2`}>
                  <LayoutDashboard className="h-4 w-4" /> {t('nav.dashboard')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className={`${dropdownItemClass} cursor-pointer gap-2 text-destructive`}>
                  <LogOut className="h-4 w-4" /> {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" className="bf-header-login">{t('nav.login')}</Button>
              </Link>
              <Link to="/register" data-testid="header-primary-cta-button">
                <Button className="bf-premium-cta bf-header-cta">
                  <span className="relative z-10">{t('nav.register')}</span>
                </Button>
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <Button variant="ghost" size="icon" className="bf-action-chip" onClick={toggleTheme} data-testid="theme-toggle-mobile" title={isDark ? t('theme.switch_to_light') : t('theme.switch_to_dark')} aria-label={isDark ? t('theme.switch_to_light') : t('theme.switch_to_dark')}>
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="bf-action-chip" onClick={toggleLang} data-testid="lang-toggle-mobile" title={lang === 'en' ? t('lang.switch_to_ar') : t('lang.switch_to_en')} aria-label={lang === 'en' ? t('lang.switch_to_ar') : t('lang.switch_to_en')}>
            <span className="text-xs font-bold">{lang === 'en' ? t('lang.short_ar') : t('lang.short_en')}</span>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="bf-menu-button" data-testid="header-mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={isRTL ? 'left' : 'right'} className="bf-mobile-sheet w-[min(92vw,390px)] p-0">
              <SheetTitle className="sr-only">Main menu</SheetTitle>
              <SheetDescription className="sr-only">Primary navigation links, language courses, placement test links, and account actions.</SheetDescription>
              <div className="flex h-full flex-col">
                <div className="bf-mobile-brand border-b border-border/70 p-4">
                  <Link to="/" className="bf-header-logo w-fit" onClick={() => setOpen(false)}>
                    <span className="bf-header-logo-glow" aria-hidden="true" />
                    <img src={isDark ? '/befluent-logo-white.png' : '/befluent-logo.png'} alt={t('brand.alt')} className="relative h-10 w-auto" />
                  </Link>
                  <div className="bf-mobile-signal" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>

                <div className="bf-mobile-menu-body space-y-5 overflow-x-hidden overflow-y-auto px-4 py-5">
                  <div className="bf-mobile-section">
                    <SheetClose asChild><Link to="/" className={mobileNavLinkClass(location.pathname === '/')}>{t('nav.home')}</Link></SheetClose>
                    <SheetClose asChild><Link to="/corporate" className={mobileNavLinkClass(location.pathname === '/corporate')}>{t('nav.corporate')}</Link></SheetClose>
                    <SheetClose asChild><Link to="/about" className={mobileNavLinkClass(location.pathname === '/about')}>{t('nav.about')}</Link></SheetClose>
                    <SheetClose asChild><Link to="/contact" className={mobileNavLinkClass(location.pathname === '/contact')}>{t('nav.contact')}</Link></SheetClose>
                  </div>

                  <div>
                    <p className="bf-mobile-section-title">{t('nav.languages')}</p>
                    <Accordion type="multiple" className="space-y-2">
                      {staticLanguages.map((language) => renderMobileLanguageLinks(language))}
                    </Accordion>
                  </div>

                  <div>
                    <p className="bf-mobile-section-title">{t('nav.placement_test')}</p>
                    <div className="bf-mobile-section space-y-2">
                      {placementLanguages.map((language) => renderMobilePlacementLinks(language))}
                    </div>
                  </div>
                </div>

                <div className="mt-auto space-y-2 border-t border-border/70 bg-muted/20 p-4">
                  {user ? (
                    <>
                      <SheetClose asChild>
                        <Link to={getDashboardPath()} className="bf-mobile-secondary-action">{t('nav.dashboard')}</Link>
                      </SheetClose>
                      <Button className="bf-mobile-secondary-action w-full" variant="ghost" onClick={() => { logout(); setOpen(false); }}>{t('nav.logout')}</Button>
                    </>
                  ) : (
                    <>
                      <SheetClose asChild><Link to="/login" className="bf-mobile-secondary-action">{t('nav.login')}</Link></SheetClose>
                      <SheetClose asChild><Link to="/register" className="bf-premium-cta bf-mobile-primary-action"><span className="relative z-10">{t('nav.register')}</span></Link></SheetClose>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
